import { useState } from "react";
import { Copy, Check } from "lucide-react";

const KEYWORDS = new Set([
  "import","struct","class","enum","let","var","func","return","if","else",
  "for","in","while","switch","case","default","break","continue","guard",
  "private","public","internal","protected","static","final","extension",
  "protocol","throws","try","catch","do","async","await","some","any","Self","self","init","mutating","weak","unowned","nil","true","false"
]);
const TYPES = new Set([
  "View","Text","VStack","HStack","ZStack","Image","Button","List","ForEach","NavigationStack","NavigationLink","ScrollView","Spacer","Color","Font","Bool","Int","Double","String","Date","UUID","Array","Dictionary","Set","Optional","TabView","Form","Section","Toggle","Picker","Stepper","Slider","TextField","SecureField","Group","LazyVStack","LazyHStack","GeometryReader","ContentView"
]);

function tokenize(line) {
  // very small Swift-ish highlighter
  const out = [];
  let i = 0;
  while (i < line.length) {
    const c = line[i];
    // comment
    if (c === "/" && line[i + 1] === "/") {
      out.push(<span key={i} className="tok-comment">{line.slice(i)}</span>);
      i = line.length;
      continue;
    }
    // string
    if (c === '"') {
      let j = i + 1;
      while (j < line.length && line[j] !== '"') {
        if (line[j] === "\\") j += 2; else j += 1;
      }
      j = Math.min(j + 1, line.length);
      out.push(<span key={i} className="tok-string">{line.slice(i, j)}</span>);
      i = j;
      continue;
    }
    // number
    if (/[0-9]/.test(c)) {
      let j = i;
      while (j < line.length && /[0-9.]/.test(line[j])) j += 1;
      out.push(<span key={i} className="tok-num">{line.slice(i, j)}</span>);
      i = j;
      continue;
    }
    // identifier
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < line.length && /[A-Za-z0-9_]/.test(line[j])) j += 1;
      const word = line.slice(i, j);
      if (KEYWORDS.has(word)) out.push(<span key={i} className="tok-keyword">{word}</span>);
      else if (TYPES.has(word)) out.push(<span key={i} className="tok-type">{word}</span>);
      else if (line[j] === "(") out.push(<span key={i} className="tok-fn">{word}</span>);
      else out.push(<span key={i}>{word}</span>);
      i = j;
      continue;
    }
    out.push(<span key={i}>{c}</span>);
    i += 1;
  }
  return out;
}

export default function CodeBlock({ code = "", language = "swift", filename = "ContentView.swift" }) {
  const [copied, setCopied] = useState(false);
  const lines = code.replace(/\r/g, "").split("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="code-card relative" data-testid="code-block">
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
          <span className="w-3 h-3 rounded-full bg-[#28C840]" />
          <span className="ml-3 text-xs text-white/50">{filename}</span>
        </div>
        <button
          onClick={copy}
          className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/5 transition"
          data-testid="code-copy-button"
        >
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
        </button>
      </div>
      <pre className="overflow-x-auto leading-relaxed">
        <code className="block">
          {lines.map((line, idx) => (
            <div key={idx} className="flex">
              <span className="text-white/25 select-none w-8 inline-block text-right pr-3 tabular-nums">
                {idx + 1}
              </span>
              <span className="flex-1 whitespace-pre">{tokenize(line) || " "}</span>
            </div>
          ))}
        </code>
      </pre>
    </div>
  );
}
